import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Heart, Instagram } from "lucide-react";
import { galleryImages } from "../data/menu";

const Gallery = () => {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  const openAt = (i) => {
    setIdx(i);
    setOpen(true);
  };

  const prev = useCallback(
    () => setIdx((i) => (i - 1 + galleryImages.length) % galleryImages.length),
    []
  );
  const next = useCallback(
    () => setIdx((i) => (i + 1) % galleryImages.length),
    []
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, prev, next]);

  const spanClass = (s) => {
    if (s === "tall") return "row-span-2";
    if (s === "wide") return "col-span-2";
    return "";
  };

  return (
    <section
      id="galeria"
      className="relative py-24 sm:py-32 bg-[var(--sn-bg)] overflow-hidden"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="h-px w-10 bg-[var(--sn-gold)]" />
            <span className="text-[var(--sn-gold)] tracking-[0.3em] text-xs uppercase flex items-center gap-2">
              <Instagram className="w-4 h-4" /> Siga no Instagram
            </span>
            <span className="h-px w-10 bg-[var(--sn-gold)]" />
          </div>
          <h2
            className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--sn-cream)] mb-4"
            data-testid="gallery-headline"
          >
            @santo.nhoque
          </h2>
          <p className="text-[var(--sn-cream-soft)]/80 max-w-2xl mx-auto mb-6">
            Acompanhe nosso dia a dia — da farinha ao prato pronto.
          </p>
          <a
            href="https://instagram.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-[var(--sn-gold)] text-[var(--sn-gold)] px-6 py-2 rounded-sm text-sm hover:bg-[var(--sn-gold)] hover:text-[var(--sn-bg-deep)] transition-all"
          >
            Seguir Perfil
          </a>
        </div>

        <div
          className="grid grid-cols-2 md:grid-cols-4 auto-rows-[200px] sm:auto-rows-[220px] gap-3 sm:gap-4"
          data-testid="gallery-grid"
        >
          {galleryImages.map((img, i) => (
            <motion.button
              key={i}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              onClick={() => openAt(i)}
              className={`group relative overflow-hidden ${spanClass(
                img.span
              )} bg-[var(--sn-bg-surface)] sn-frame`}
              data-testid={`gallery-item-${i}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--sn-bg-deep)]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-black/20">
                <div className="flex items-center gap-2 text-white">
                  <Heart className="w-8 h-8 fill-white" />
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[80] bg-[#0a120d]/95 backdrop-blur-md flex items-center justify-center"
            onClick={() => setOpen(false)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute top-5 right-5 w-11 h-11 rounded-full border border-[var(--sn-gold)] text-[var(--sn-gold)] flex items-center justify-center hover:bg-[var(--sn-gold)]/10 transition-colors"
              onClick={() => setOpen(false)}
              aria-label="Fechar"
              data-testid="lightbox-close"
            >
              <X size={20} />
            </button>

            <button
              className="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-[var(--sn-gold)] text-[var(--sn-gold)] flex items-center justify-center hover:bg-[var(--sn-gold)]/10 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              aria-label="Anterior"
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={22} />
            </button>

            <button
              className="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-[var(--sn-gold)] text-[var(--sn-gold)] flex items-center justify-center hover:bg-[var(--sn-gold)]/10 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              aria-label="Próxima"
              data-testid="lightbox-next"
            >
              <ChevronRight size={22} />
            </button>

            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="max-w-[88vw] max-h-[82vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={galleryImages[idx].src}
                alt={galleryImages[idx].alt}
                className="max-w-full max-h-[82vh] object-contain sn-frame"
              />
              <p className="text-center text-[var(--sn-cream-soft)]/80 text-sm mt-4 font-serif-alt italic">
                {galleryImages[idx].alt}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
